/**
 * Utilitários compartilhados do Controle PLD (Task #190 Fase 2).
 *
 * Cores por submercado, formatadores R$/MWh + data BR, badge de status de
 * semana operativa, lista de meses pro dropdown e mapeamento dia→revisão.
 * Reusa tokens da identidade Indra (turquesa/varejo) + 2 cores pros demais
 * submercados, mantendo contraste no fundo escuro da Mesa.
 */
import type {
  PldSemanaOperativa,
  PldSemanaStatus,
  PldSubmercado,
} from "../../../api/types";

export const SUBMERCADOS: PldSubmercado[] = ["SE", "S", "NE", "N"];

/** Rótulos longos pros dropdowns/legendas. */
export const SUBMERCADO_LABEL: Record<PldSubmercado, string> = {
  SE: "Sudeste/CO",
  S: "Sul",
  NE: "Nordeste",
  N: "Norte",
};

/** Cor por submercado (séries de gráfico + chips). SE = turquesa Indra,
 * S = azul varejo; NE/N = âmbar/roxo pra contraste no escuro. */
export const SUBMERCADO_COLOR: Record<PldSubmercado, string> = {
  SE: "#00DAC8",
  S: "#4FB3E8",
  NE: "#F5A623",
  N: "#B084EB",
};

const MESES_CURTO = [
  "jan", "fev", "mar", "abr", "mai", "jun",
  "jul", "ago", "set", "out", "nov", "dez",
];

/** "2026-05" → "mai/26". */
export function mesLabel(mesRef: string): string {
  const [y, m] = mesRef.split("-");
  const idx = parseInt(m, 10) - 1;
  return `${MESES_CURTO[idx] ?? m}/${y.slice(2)}`;
}

/** "2026-05-01" → "01/05/2026". */
export function fmtDateBR(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

const DIAS_SEMANA = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

/** Dia da semana abreviado de uma data ISO (sem fuso — usa UTC pra não
 * deslocar perto da meia-noite local). */
export function diaSemanaBR(iso: string): string {
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  return DIAS_SEMANA[dt.getUTCDay()] ?? "";
}

/** PLD R$/MWh com 2 casas pt-BR, ou "—" quando ausente. */
export function fmtPld(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return `R$ ${v.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Só o número (sem "R$"), pra eixos/células compactas. */
export function fmtPldNum(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return v.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Cor por sinal de um delta/diferença — paleta compartilhada (DIF DECOMP #217,
 * Δ NE−SE #223). Negativo (abaixo) = vermelho; positivo (acima) = turquesa. */
export function classBySign(v: number): string {
  if (v < 0) return "text-red-400";
  if (v > 0) return "text-indra-turquesa";
  return "text-white/50";
}

/** Delta SINALIZADO com prefixo +/− e 2 casas (R$); "—" quando não calculável. */
export function fmtDif(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const s = fmtPld(Math.abs(v));
  if (v > 0) return `+${s}`;
  if (v < 0) return `−${s}`;
  return s;
}

export interface MesOption {
  value: string; // "2026-05"
  label: string; // "mai/26"
}

/** Os N meses terminando no mês de `hoje` (mais recente primeiro). */
export function ultimosMeses(n: number, hoje: Date = new Date()): MesOption[] {
  const out: MesOption[] = [];
  const y = hoje.getFullYear();
  const m = hoje.getMonth(); // 0-based
  for (let i = 0; i < n; i++) {
    const d = new Date(y, m - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    out.push({ value, label: mesLabel(value) });
  }
  return out;
}

/** "2026-05" do mês corrente (default do dropdown). */
export function mesRefCorrente(hoje: Date = new Date()): string {
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
}

export interface StatusBadgeStyle {
  label: string;
  className: string;
}

/** Estilo do badge de status de semana operativa. */
export function statusBadge(status: PldSemanaStatus): StatusBadgeStyle {
  switch (status) {
    case "realizada":
      return { label: "Realizada", className: "bg-emerald-400/15 text-emerald-300" };
    case "em_curso":
      return { label: "Em curso", className: "bg-indra-turquesa/20 text-indra-turquesa" };
    case "pendente":
    default:
      return { label: "Pendente", className: "bg-white/10 text-white/50" };
  }
}

/** Acha a revisão (REV0-n) cuja janela contém a data ISO. "" se nenhuma. */
export function revisaoDaData(iso: string, semanas: PldSemanaOperativa[]): string {
  for (const s of semanas) {
    if (iso >= s.periodo_inicio && iso <= s.periodo_fim) return s.revisao;
  }
  return "";
}
